import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { useAuthStore } from '../store/authStore';
import { getPendingDevices, approveDevice, rejectDevice, PendingDevice } from '../services/api';
import { parseUtc } from '../utils/dataAge';
import './AdminPanel.css';

const PROTOCOL_LABELS: Record<string, string> = {
  horta: 'Horta',
  albatross: 'Albatross',
};

const formatFirstSeen = (iso: string | null): string => {
  if (!iso) return '—';
  return parseUtc(iso).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [devices, setDevices] = useState<PendingDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingIds, setActingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (user && !user.administrator) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPendingDevices();
      setDevices(data);
    } catch {
      setError('Nie udało się pobrać listy oczekujących urządzeń');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (deviceId: number, action: 'approve' | 'reject') => {
    setActingIds(prev => new Set(prev).add(deviceId));
    try {
      if (action === 'approve') await approveDevice(deviceId);
      else await rejectDevice(deviceId);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch {
      setError('Akcja się nie powiodła — spróbuj ponownie');
    } finally {
      setActingIds(prev => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  if (user && !user.administrator) return null;

  return (
    <div className="admin-panel-layout">
      <Header />
      <div className="admin-panel-body">
        <div className="admin-panel-container">
          <div className="admin-panel-title-bar">
            <h1 className="admin-panel-title">Urządzenia oczekujące na zatwierdzenie</h1>
          </div>

          {error && <div className="admin-panel-error">{error}</div>}

          {loading ? (
            <div className="admin-panel-loading">Ładowanie…</div>
          ) : (
            <div className="admin-panel-table-wrapper">
              <table className="admin-panel-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>Unikalny ID</th>
                    <th>Protokół</th>
                    <th>Pierwsze połączenie</th>
                    <th className="admin-panel-actions-th">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.length === 0 && (
                    <tr>
                      <td colSpan={5} className="admin-panel-empty">
                        Brak urządzeń oczekujących na zatwierdzenie
                      </td>
                    </tr>
                  )}
                  {devices.map(d => {
                    const busy = actingIds.has(d.id);
                    return (
                      <tr key={d.id}>
                        <td>{d.name}</td>
                        <td className="admin-panel-uniqueid">{d.uniqueid}</td>
                        <td>{d.protocol ? (PROTOCOL_LABELS[d.protocol] ?? d.protocol) : '—'}</td>
                        <td>{formatFirstSeen(d.first_seen_at)}</td>
                        <td className="admin-panel-actions">
                          <button
                            className="admin-panel-btn admin-panel-btn--approve"
                            disabled={busy}
                            onClick={() => act(d.id, 'approve')}
                          >
                            {busy ? '…' : 'Zatwierdź'}
                          </button>
                          <button
                            className="admin-panel-btn admin-panel-btn--reject"
                            disabled={busy}
                            onClick={() => act(d.id, 'reject')}
                          >
                            {busy ? '…' : 'Odrzuć'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
